import { DRAFT_TEXT_PLACEHOLDER } from '@/hooks/constants';
import { escapeSsmlText } from '@d-id/ssml';
import { IVoice, Providers, ScriptType, VoiceConfig } from '@d-id/types';

export interface TextScript {
    type: ScriptType.text;
    text: string;
    voice: IVoice;
    voiceConfig?: VoiceConfig;
    ssml?: boolean;
    subtitles?: boolean;
}
interface AudioScript {
    type: ScriptType.audio;
    audioUrl: string;
    subtitles?: boolean;
}
interface RecordScript {
    type: ScriptType.record;
    audioUrl: string;
    subtitles?: boolean;
}

export type Script = TextScript | AudioScript | RecordScript;

export const buildScript = (
    text: string,
    voice: IVoice | null,
    voiceConfig: VoiceConfig | null,
    subtitles: boolean,
    audioUrl?: string
): Script | null => {
    if (audioUrl) {
        return {
            type: ScriptType.audio,
            subtitles,
            audioUrl,
        };
    }

    if (voice) {
        return {
            type: ScriptType.text,
            text: text || DRAFT_TEXT_PLACEHOLDER,
            voice,
            subtitles,
            ...(voiceConfig ? { voiceConfig } : {}),
        };
    }

    return null;
};

export const isElevenLabsEnglishVoice = (locale: string): boolean => {
    const res = locale.split('-');
    return res.length > 1 ? res[0] === 'en' : false;
};

const isProviderSupportsSsml = (provider: Providers): boolean => {
    return [Providers.Microsoft, Providers.AzureOpenAi].includes(provider);
};

export const buildTalkTtsInput = (script: Script, reduceNoise?: boolean) => {
    if (script.type === ScriptType.text) {
        const ssml = isProviderSupportsSsml(script.voice.provider);
        return {
            type: script.type,
            input: escapeSsmlText(script.text, true),
            subtitles: script.subtitles,
            ssml,
            provider: {
                type: script.voice.provider,
                voice_id: script.voice.id,
                voice_language: script.voice.language,
                ...(script.voiceConfig ? { voice_config: script.voiceConfig } : {}),
                ...(script.voice.provider === Providers.Elevenlabs ? { model_id: script.voice.config?.modelId } : {}),
            },
        };
    }

    return {
        type: script.type,
        audio_url: script.audioUrl,
        ...(reduceNoise && { reduce_noise: reduceNoise }),
        subtitles: script.subtitles,
    };
};
